import React, {
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { VisuallyHidden } from "react-aria-components";
import { t } from "ttag";

import { TextRotatorBlock } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable, IconButton } from "../Clickables";
import { getThemeClass } from "../ThemedModule";

import styles from "./TextRotator.module.scss";

export const TextRotator = ({ value }: { value: TextRotatorBlock }) => {
    const id = useId();
    const labelId = `${id}-label`;
    const slides = value.slides ?? [];
    const count = slides.length;

    const [activeIndex, setActiveIndex] = useState(0);
    const [liveText, setLiveText] = useState("");
    const isFirstRender = useRef(true);
    const trackRef = useRef<HTMLDivElement>(null);
    const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
    const activeIndexRef = useRef(activeIndex);
    useLayoutEffect(() => {
        activeIndexRef.current = activeIndex;
    });

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.intersectionRatio > 0.6) {
                        setActiveIndex(
                            Number(
                                (entry.target as HTMLElement).dataset
                                    .slideIndex,
                            ),
                        );
                    }
                }
            },
            { threshold: 0.6, root: track },
        );
        slideRefs.current.forEach((slide) => {
            if (slide) observer.observe(slide);
        });
        return () => observer.disconnect();
    }, [count]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setLiveText(t`Slide ${activeIndex + 1} of ${count}`);
    }, [activeIndex, count]);

    // Center the active slide within the track. Uses track.scrollTo
    // (not slide.scrollIntoView) so only the local carousel scrolls —
    // scrollIntoView would also scroll the page vertically if the track
    // is below the viewport.
    const centerSlideInTrack = (
        slide: HTMLDivElement,
        behavior: ScrollBehavior,
    ) => {
        const track = trackRef.current;
        if (!track) return;
        const targetLeft =
            slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
        track.scrollTo({ left: targetLeft, behavior });
    };

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        const observer = new ResizeObserver(() => {
            const slide = slideRefs.current[activeIndexRef.current];
            if (slide) centerSlideInTrack(slide, "instant");
        });
        observer.observe(track);
        return () => observer.disconnect();
    }, []);

    const scrollTo = (index: number) => {
        const slide = slideRefs.current[index];
        if (!slide) return;
        setActiveIndex(index);
        requestAnimationFrame(() => {
            centerSlideInTrack(slide, "smooth");
            slide.focus({ preventScroll: true });
        });
    };

    return (
        <section
            className={concatClassNames([
                styles.root,
                getThemeClass(value.module_theme),
            ])}
            aria-roledescription="carousel"
            aria-labelledby={labelId}
        >
            <VisuallyHidden id={labelId}>{value.label}</VisuallyHidden>
            <VisuallyHidden aria-live="polite" aria-atomic="true">
                {liveText}
            </VisuallyHidden>
            <div className={styles.inner}>
                <div className={styles.images}>
                    {slides.map((slide, i) => (
                        <div
                            key={i}
                            className={concatClassNames([
                                styles.image,
                                i === activeIndex
                                    ? styles.imageActive
                                    : undefined,
                            ])}
                        >
                            {slide.image && (
                                <ImageChooserBlock
                                    value={slide.image}
                                    attrs={{}}
                                />
                            )}
                        </div>
                    ))}
                </div>
                <div ref={trackRef} className={styles.sentenceTrack}>
                    {slides.map((slide, i) => (
                        <div
                            key={i}
                            ref={(el) => {
                                slideRefs.current[i] = el;
                            }}
                            role="group"
                            aria-roledescription="slide"
                            aria-label={t`Slide ${i + 1} of ${count}`}
                            tabIndex={-1}
                            data-slide-index={i}
                            className={concatClassNames([
                                styles.slide,
                                i !== activeIndex
                                    ? styles.slideInactive
                                    : undefined,
                            ])}
                        >
                            {i !== activeIndex ? (
                                <Clickable
                                    className={styles.slideInner}
                                    onPress={() => scrollTo(i)}
                                    aria-label={t`Go to slide ${i + 1}`}
                                    excludeFromTabOrder={
                                        Math.abs(i - activeIndex) !== 1
                                    }
                                >
                                    <p
                                        className={styles.sentence}
                                        aria-hidden="true"
                                    >
                                        {slide.sentence}
                                    </p>
                                </Clickable>
                            ) : (
                                <div className={styles.slideInner}>
                                    <p className={styles.sentence}>
                                        {slide.sentence}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div
                    className={styles.dots}
                    role="group"
                    aria-label={t`Choose slide to display`}
                >
                    {slides.map((_, i) => (
                        <Clickable
                            key={i}
                            className={styles.dot}
                            onPress={() => scrollTo(i)}
                            data-active={i === activeIndex ? "" : undefined}
                        >
                            <VisuallyHidden>{t`Slide ${i + 1}`}</VisuallyHidden>
                        </Clickable>
                    ))}
                </div>
                <div className={styles.controls}>
                    <IconButton
                        icon="caret"
                        label={t`Previous slide`}
                        isDisabled={activeIndex === 0}
                        onPress={() => scrollTo(activeIndex - 1)}
                    />
                    <IconButton
                        icon="caret"
                        label={t`Next slide`}
                        isDisabled={activeIndex === count - 1}
                        onPress={() => scrollTo(activeIndex + 1)}
                    />
                </div>
            </div>
        </section>
    );
};
